import * as Sentry from "@sentry/react";
import { QueryClient } from "@tanstack/react-query";

// Global token store - set by AuthProvider when session changes
// This avoids calling supabase.auth.getSession() which fails in cross-site iframe contexts
let storedAccessToken: string | null = null;

export function setStoredAccessToken(token: string | null) {
  storedAccessToken = token;
}

export function getStoredAccessToken(): string | null {
  return storedAccessToken;
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  if (storedAccessToken) {
    return {
      'Authorization': `Bearer ${storedAccessToken}`,
    };
  }
  
  return {};
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: async ({ queryKey }) => {
        const authHeaders = await getAuthHeaders();
        
        // Build URL from queryKey segments
        // Support multiple patterns:
        // - Single string: ['/api/endpoint'] -> /api/endpoint
        // - Path segments: ['/api/endpoint', id] -> /api/endpoint/id
        // - Query string: ['/api/endpoint', '?param=value'] -> /api/endpoint?param=value
        let url: string;
        if (queryKey.length === 1) {
          url = queryKey[0] as string;
        } else {
          const base = queryKey[0] as string;
          const rest = queryKey.slice(1).map(segment => String(segment));
          
          const pathSegments = rest.filter(s => !s.startsWith('?'));
          const queryStringSegment = rest.find(s => s.startsWith('?'));
          const pathUrl = pathSegments.length > 0 ? [base, ...pathSegments].join('/') : base;
          url = queryStringSegment ? pathUrl + queryStringSegment : pathUrl;
        }
        
        const res = await fetch(url, {
          credentials: "include",
          headers: authHeaders,
        });

        if (!res.ok) {
          // Try to parse JSON error response for detailed error info
          let errorMessage = `${res.status}: ${res.statusText}`;
          try {
            const errorData = await res.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
            if (errorData.error_type && errorData.error_type !== 'Error') {
              errorMessage = `[${errorData.error_type}] ${errorMessage}`;
            }
          } catch {
            try {
              const text = await res.text();
              if (text) errorMessage = `${res.status}: ${text}`;
            } catch {}
          }
          const error = new Error(errorMessage);
          // Report 5xx query errors to Sentry
          if (res.status >= 500) {
            Sentry.captureException(error, {
              tags: { source: 'react-query', url },
              extra: { statusCode: res.status },
            });
          }
          throw error;
        }

        return res.json();
      },
    },
    mutations: {
      onError: (error) => {
        console.error("Mutation error:", error);
        // Capture all mutation errors to Sentry
        if (error instanceof Error) {
          const is5xx = /^5\d\d/.test(error.message);
          Sentry.captureException(error, {
            level: is5xx ? 'error' : 'warning',
            tags: { source: 'react-query-mutation' },
          });
        }
      },
    },
  },
});

type RequestOptions = {
  method: string;
  headers?: Record<string, string>;
  body?: unknown;
};

async function handleRequest(url: string, options: RequestOptions) {
  const authHeaders = await getAuthHeaders();
  
  // Detect if body is FormData to handle multipart uploads
  const isFormData = options.body instanceof FormData;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      // Don't set Content-Type for FormData (browser will set it with boundary)
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...authHeaders,
      ...options.headers,
    },
    // Don't JSON.stringify FormData
    body: isFormData 
      ? options.body as FormData
      : (options.body ? JSON.stringify(options.body) : undefined),
    credentials: "include",
  });

  if (!response.ok) {
    // Try to parse JSON error response for detailed error info
    let errorMessage = `${response.status}: ${response.statusText}`;
    try {
      const errorData = await response.json();
      // Use detailed error message if available
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
      // Include debug info if available
      if (errorData.error_type && errorData.error_type !== 'Error') {
        errorMessage = `[${errorData.error_type}] ${errorMessage}`;
      }
    } catch {
      // If JSON parsing fails, try to get text
      try {
        const text = await response.text();
        if (text) {
          errorMessage = `${response.status}: ${text}`;
        }
      } catch {
        // Use default error message
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function apiRequest(
  url: string,
  options: Omit<RequestOptions, "method"> & { method?: string } = {}
) {
  return handleRequest(url, {
    method: options.method || "POST",
    ...options,
  });
}
